<template lang="pug">
  .width-100
    v-popover.item(
      placement='top-end'
      :delay='{show: 100, hide: 600}'
      trigger='hover click'
      popoverClass='krea-actions-popover'
      :container='false'
      v-if='isStudentTab ? activeLocalizedResource.mediaStudent : activeLocalizedResource.mediaTeacher'
    )
      #media.column
        img.media-picture.m-bottom-1(
          :src='isStudentTab ? activeLocalizedResource.mediaStudent : activeLocalizedResource.mediaTeacher'
          v-if='mediaIsNotVideo'
          :alt='activeLocalizedResource.title'
        )
        .media-presentation.width-100(
          v-else
          :src='isStudentTab ? activeLocalizedResource.mediaStudent : activeLocalizedResource.mediaTeacher'
          v-html='mediaHtml'
          height='400px'
        )
      template(slot='popover')
        actions(
          file fileLink
          fileType='image'
          placement='end'
          :fileValue='isStudentTab ? activeLocalizedResource.mediaStudent : activeLocalizedResource.mediaTeacher'
          @removeFile='removeMedia'
          :updateFile='editResourceImage'
          :info='isStudentTab ? mediaStudentInfo : mediaTeacherInfo'
        )
    #media.media-box-empty.column.center.middle(v-else)
      krea-file-uploader.media-box-uploader(
        :navigationLanguage='false'
        link type='image' linkType='video'
        :updateFile='editResourceImage'
        :updateLink='editResourceVideo'
      )
    krea-text-area.m-top-1.m-bottom-2(
      edit
      height='50px'
      :value='isStudentTab ? activeLocalizedResource.mediaStudentSummary : activeLocalizedResource.mediaTeacherSummary'
      :name='isStudentTab ? "mediaStudentSummary" : "mediaTeacherSummary"'
      :info='isStudentTab ? mediaSummaryStudentInfo : mediaSummaryTeacherInfo'
      :updateMethod='editMediaSummary'
      :placeholder='innerI18n.t("krea.resource.teacher.triggerPlaceholder")'
    )
    .width-100.custom-fields.column(v-if='customFields && customKreaFields.length')
      krea-custom-field(
        v-for='field of customKreaFields'
        :key='field.id'
        :field='field'
        edit
      )
    .column.m-bottom-2
      #rhetoricalQuestion.m-bottom-2(v-if='isStudentTab')
        h3.title-medium.m-bottom-1 {{ innerI18n.t('krea.resource.student.rhetoricalQuestion') }}
        krea-text-area(
          edit
          name='rhetoricalQuestion'
          :info='rhetoricalQuestionInfo'
          :value='activeLocalizedResource.rhetoricalQuestion'
          :updateMethod='editResourceField'
          :maxCharacters='300'
          placeholderSize='big'
          size='big'
          :placeholder='innerI18n.t("krea.resource.presentationTab.rhetoricalQuestionPlaceholder")'
          height='150px'
        )
      #classPlanDescription.m-bottom-2(v-if='showClassPlan')
        h3.title-medium.m-bottom-1 {{ innerI18n.t('teacher.classPlan') }}
        .md-content(v-if='classPlanDescriptionDisabled && activeLocalizedResource.classPlans.length > 1')
          ul
            li(v-for='resClass in activeLocalizedResource.classPlans')
              router-link(:to='{name: routes.kreaResourceTeacherClass, params: {classId: resClass.classNumber}}')
                | {{ getClassListItem(resClass) }}
        krea-text-editor(
          v-else
          @finishEdition='disableClassPlan'
          edit
          video image strike underline bold orderedList unorderedList removeMarks heading italic undo redo linkOption removeLink quote
          placeholderHeight='200px'
          height='400px'
          @input='editResourceField'
          name="classPlanDescription"
          :value='activeLocalizedResource.classPlanDescription || ""'
          :placeholder='innerI18n.t("krea.resource.teacher.classPlanPlaceholder")'
          :info='!isStudentTab ? classPlanInfoTeacher : null'
        )
      div(:id='isStudentTab ? "studentPedagogicalGoal" : "pedagogicalGoal"')
        h3.title-medium.m-bottom-1 {{ innerI18n.t('krea.resource.teacher.resourceObjectiveLabel') }}
        krea-text-editor(
          edit
          video image strike underline bold orderedList unorderedList removeMarks heading italic undo redo linkOption removeLink quote
          placeholderHeight='200px'
          height='400px'
          :info='!isStudentTab ? pedagogicalObjectiveInfoTeacher : pedagogicalObjectiveInfoStudent'
          @input='editResourceField'
          :name='isStudentTab ? "studentPedagogicalGoal" : "pedagogicalGoal"'
          :value='isStudentTab ? (activeLocalizedResource.studentPedagogicalGoal || "") : (activeLocalizedResource.pedagogicalGoal || "")'
          :placeholder='innerI18n.t("krea.resource.teacher.resourceObjectivePlaceholder")'
        )
      .row.center.m-top-4.width-100
        button.primary-button(@click='addClass') {{ innerI18n.t("krea.resource.sidebar.createClass") }}
</template>

<script>
import KreaTextEditor from '@/components/Krea/Inputs/KreaTextEditor'
import Actions from '@/components/Krea/Inputs/Actions/Actions'
import KreaFileUploader from '@/components/Krea/Inputs/KreaFileUploader'
import KreaTextArea from '@/components/Krea/Inputs/KreaTextArea'
import { innerI18n } from '../../../main'
import { routes } from '../../../router'
import { mapActions, mapState } from 'vuex'
import { languagesCodesToString } from '../../../constants/languages'
import { resourceMediaTypes } from '../../../utils/constants'
import { getExtension, isImage } from '../../../utils/file'
import { getOEmbedHtml, getOEmbedUrl } from '../../../utils/embed'
import kreaResourceRedirection from '@/mixins/kreaResourceRedirection'
import { kreaCustomFieldTypes } from '@/constants/kreaCustom'
import KreaCustomField from '../Inputs/KreaCustomField'

export default {
  mixins: [kreaResourceRedirection],
  components: { KreaTextEditor, Actions, KreaFileUploader, KreaTextArea, KreaCustomField },
  data() {
    return {
      innerI18n,
      routes,
      mediaHtml: null,
      classPlanDescriptionDisabled: false
    }
  },
  watch: {
    async activeLocalizedResource() {
      if (!this.mediaIsNotVideo) await this.setMediaHtml()
    }
  },
  async mounted() {
    if (!this.mediaIsNotVideo) await this.setMediaHtml()
    if (this.activeLocalizedResource.classPlanDescription) {
      this.classPlanDescriptionDisabled = false
    } else {
      this.classPlanDescriptionDisabled = true
    }
  },
  computed: {
    ...mapState({
      resource: state => state.kreaResource.resource,
      activeLanguage: state => state.kreaResource.activeLanguage,
      innerLocale: state => state.behavior.innerLocale,
      customFields: state => state.userInfo.customFields
    }),
    studentCustomFields() {
      return this.customFields && this.customFields.customizedKreaFields
        .filter(field => field.type === kreaCustomFieldTypes.resource && field.location === 'studentTrigger')
    },
    teacherCustomFields() {
      return this.customFields && this.customFields.customizedKreaFields
        .filter(field => field.type === kreaCustomFieldTypes.resource && field.location === 'teacherTrigger')
    },
    customKreaFields() {
      return this.isStudentTab ? this.studentCustomFields : this.teacherCustomFields
    },
    showClassPlan() {
      return !this.isStudentTab &&
      (this.activeLocalizedResource.classPlanDescription ||
      this.activeLocalizedResource.classPlans.length > 1 ||
      !this.classPlanDescriptionDisabled
      )
    },
    kreaContentGuide() {
      return process.env[`VUE_APP_KREA_CREATE_CONTENT_GUIDE_${this.innerLocale}`]
    },
    activeLocalizedResource() {
      return this.resource.localizedResources.find(res => languagesCodesToString[res.locale] === this.activeLanguage)
    },
    isStudentTab() {
      return this.$route.name === routes.kreaResourceStudentTrigger
    },
    mediaIsNotVideo() {
      return this.isStudentTab
        ? this.activeLocalizedResource.mediaStudentType !== resourceMediaTypes.video || isImage(getExtension(this.activeLocalizedResource.mediaStudent))
        : this.activeLocalizedResource.mediaTeacherType !== resourceMediaTypes.video || isImage(getExtension(this.activeLocalizedResource.mediaTeacher))
    },
    rhetoricalQuestionInfo() {
      return {
        title: innerI18n.t('krea.resource.presentationTab.rhetoricalQuestionInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.presentationTab.rhetoricalQuestionInfo')]
      }
    },
    classPlanInfoTeacher() {
      return {
        title: innerI18n.t('krea.resource.teacher.classPlanInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.teacher.classPlanInfo')]
      }
    },
    pedagogicalObjectiveInfoTeacher() {
      return {
        title: innerI18n.t('krea.resource.teacher.pedagogicalObjectiveInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.teacher.pedagogicalObjectiveInfo')]
      }
    },
    pedagogicalObjectiveInfoStudent() {
      return {
        title: innerI18n.t('krea.resource.student.pedagogicalObjectiveInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.student.pedagogicalObjectiveInfo')]
      }
    },
    mediaTeacherInfo() {
      return {
        title: innerI18n.t('krea.resource.teacher.mediaTeacherInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.teacher.mediaTeacherInfo')]
      }
    },
    mediaSummaryTeacherInfo() {
      return {
        title: innerI18n.t('krea.resource.teacher.mediaSummaryTeacherInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.teacher.mediaSummaryTeacherInfo')]
      }
    },
    mediaStudentInfo() {
      return {
        title: innerI18n.t('krea.resource.student.mediaStudentInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.student.mediaStudentInfo')]
      }
    },
    mediaSummaryStudentInfo() {
      return {
        title: innerI18n.t('krea.resource.student.mediaSummaryStudentInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.student.mediaSummaryStudentInfo')]
      }
    }
  },
  methods: {
    ...mapActions([
      'editKreaLocalizedResourceField',
      'addKreaResourceClass'
    ]),
    async setMediaHtml() {
      let media
      if (this.isStudentTab) {
        media = this.activeLocalizedResource.mediaStudent
      } else {
        media = this.activeLocalizedResource.mediaTeacher
      }
      const oembedUrl = getOEmbedUrl(media)
      const html = await getOEmbedHtml(oembedUrl)
      this.mediaHtml = html
    },
    editResourceField(value, field) {
      this.editKreaLocalizedResourceField({ value, field, locale: this.activeLanguage })
    },
    addClass() {
      this.addKreaResourceClass(this.activeLanguage)
      this.$router.push({
        name: this.isStudentTab ? routes.kreaResourceStudentClass : routes.kreaResourceTeacherClass,
        params: { classId: this.activeLocalizedResource.classPlans.length }
      })
    },
    removeMedia() {
      this.editResourceVideo(null)
      this.editResourceImage(null)
    },
    editResourceImage(value) {
      if (this.isStudentTab) {
        this.editKreaLocalizedResourceField({ field: 'mediaStudent', value, locale: this.activeLanguage })
        this.editKreaLocalizedResourceField({ field: 'mediaStudentType', value: resourceMediaTypes.image, locale: this.activeLanguage })
      } else {
        this.editKreaLocalizedResourceField({ field: 'mediaTeacher', value, locale: this.activeLanguage })
        this.editKreaLocalizedResourceField({ field: 'mediaTeacherType', value: resourceMediaTypes.image, locale: this.activeLanguage })
      }
    },
    editResourceVideo(value) {
      if (this.isStudentTab) {
        this.editKreaLocalizedResourceField({ field: 'mediaStudent', value, locale: this.activeLanguage })
        this.editKreaLocalizedResourceField({ field: 'mediaStudentType', value: resourceMediaTypes.video, locale: this.activeLanguage })
      } else {
        this.editKreaLocalizedResourceField({ field: 'mediaTeacher', value, locale: this.activeLanguage })
        this.editKreaLocalizedResourceField({ field: 'mediaTeacherType', value: resourceMediaTypes.video, locale: this.activeLanguage })
      }
    },
    editMediaSummary(value, field) {
      this.editKreaLocalizedResourceField({ value, field, locale: this.activeLanguage })
    },
    getClassListItem(resClass) {
      return resClass.name
        ? `${innerI18n.t('sidebar.class')} ${resClass.classNumber}: ${resClass.name}`
        : `${innerI18n.t('sidebar.class')} ${resClass.classNumber}`
    },
    disableClassPlan() {
      if (!this.activeLocalizedResource.classPlanDescription) {
        this.classPlanDescriptionDisabled = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';
@import 'src/scss/variables/sizes';
@import 'src/scss/common/texts';

.custom-fields{
  gap: 10px;
}

.media-presentation {
  border: 0;
  height: 400px;
}

.media-picture {
  width: 100%;
  max-height: 400px;
  object-fit: cover;
}

.media-box-empty{
  height: 400px;
  border: 1px solid $light-gray;
  .media-box-uploader{
    width: 400px;
  }
}
</style>
